import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Modal } from '../../../shared/components/Modal';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useInstagramConnections, useResetInstagramConnection } from '../hooks/useInstagram';
import { InstagramFetchHealth, InstagramStatusPill } from './InstagramConnectionFacts';
import { formatInstagramDate } from '../format';

interface Props {
  merchantId: string;
}

/**
 * TCS-admin read-only view of ONE merchant's Instagram connection, rendered on the merchant
 * detail page — the staff-side counterpart to the merchant's own /settings/instagram page.
 *
 * Read-only by necessity, not preference: the connection is authorised by the merchant on
 * Instagram itself, and watchman's `me/instagram/*` routes resolve the merchant from the JWT,
 * so a COLLECTIVE session cannot drive them however the merchant is pinned. The one action
 * staff DO have is Reset (MERCHANT.UPDATE), the support escape hatch for a stuck token.
 *
 * Sourced from the admin `GET instagram/connections` list rather than a by-id route, because
 * osprey-api exposes no per-merchant admin read. The list is small (one row per merchant that
 * has ever connected) and is already cached by the connections page.
 */
export function MerchantInstagramPanel({ merchantId }: Props) {
  const { hasPermission } = usePermissions();
  const canReset = hasPermission(Permission.MERCHANT_UPDATE);

  const { data: connections, isLoading, error } = useInstagramConnections();
  const resetConnection = useResetInstagramConnection();
  const [confirmReset, setConfirmReset] = useState(false);

  const connection = connections?.find((c) => c.merchantId === merchantId);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-foreground">Instagram Feed</h2>
        {connection && canReset && (
          <Button variant="outline" size="sm" onClick={() => setConfirmReset(true)}>
            Reset
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-foreground">Loading Instagram connection…</p>}
      {error && <p className="text-sm text-red-500">Failed to load the Instagram connection.</p>}

      {!isLoading && !error && !connection && (
        <p className="text-sm text-foreground">
          This merchant has never connected Instagram. They authorise it themselves from their
          own admin — there is no TCS-side connect.
        </p>
      )}

      {!isLoading && !error && connection && (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-foreground">Status</dt>
            <dd>
              <InstagramStatusPill status={connection.status} />
            </dd>
          </div>
          <div>
            <dt className="text-foreground">Storefront section</dt>
            <dd className="font-medium text-foreground">
              {connection.feedEnabled ? 'Shown' : 'Hidden'}
            </dd>
          </div>
          <div>
            <dt className="text-foreground">Account</dt>
            <dd className="font-medium text-foreground">
              {connection.instagramUsername ? `@${connection.instagramUsername}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-foreground">Token expires</dt>
            <dd className="font-medium text-foreground">
              {formatInstagramDate(connection.tokenExpiresAt)}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-foreground">Last refresh</dt>
            <dd className="font-medium">
              <InstagramFetchHealth
                lastFetchAt={connection.lastFetchAt}
                lastFetchSucceeded={connection.lastFetchSucceeded}
                lastFetchError={connection.lastFetchError}
              />
            </dd>
          </div>
        </dl>
      )}

      <Modal isOpen={confirmReset} onClose={() => setConfirmReset(false)} size="sm">
        <div className="p-6 space-y-4">
          <h2 className="text-[16px] font-semibold text-foreground">Reset this connection?</h2>
          <p className="text-[14px] text-foreground">
            The stored access token is cleared and the connection goes back to Not connected.
            The merchant's storefront Instagram section disappears until they reconnect.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="sm" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await resetConnection.mutateAsync(merchantId);
                setConfirmReset(false);
              }}
              disabled={resetConnection.isPending}
              loading={resetConnection.isPending}
            >
              Reset
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
